import React, {
  createContext,
  useEffect,
  useMemo,
  useContext,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from './apiClient';
import { ContextSearch } from './ContextSearch';
import enumUserRole from '../enum/enumUserRole';

const initialState = {
  isLoading: true,
  signedIn: false,
  currentUser: null,
  currentOrganization: null,
  handleUpdateCurrentOrganization: () => {},
  handleLogin: () => {},
  handleLogout: () => {},
  handleForgot: () => {},
  handleReset: () => {},
  getRedirectUrl: () => {},
};

const tokenName = 'JWT';

export const ContextUser = createContext(initialState);

const UserProvider = ({ children }) => {
  const { reset } = useContext(ContextSearch);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [userHierarchy, setUserHierarchy] = useState(null);

  const saveToken = (tokenString) => {
    localStorage.setItem(tokenName, tokenString);
  };

  const removeToken = () => localStorage.removeItem(tokenName);

  const loginRedirectRules = {
    ADMIN: {
      path: '/',
    },
    ACCESS_STAFF: {
      path: '/',
    },
    ACCESS_ORG_LEVEL: {
      path: '/prefeitura',
    },
    ACCESS_BRANCH_LEVEL: {
      path: '/prefeitura/secretaria',
    },
    ACCESS_UNIT_LEVEL: {
      path: '/prefeitura/secretaria/unidade',
    },
    ACCESS_DEPARTMENT_LEVEL: {
      path: '/prefeitura/secretaria/unidade/departamento',
    },
  };

  const getRedirectUrl = (role, allowedId) => {
    if (allowedId === undefined) {
      return '/sem-permissao';
    }

    if (allowedId === 'all') {
      return '/';
    }

    return `${loginRedirectRules[role].path}/${allowedId}`;
  };

  const isPathValidForUser = (currentPath, role, allowedId) => {
    // Admin pode acessar qualquer rota
    if (role === 'ADMIN' || role === 'ACCESS_STAFF') {
      return true;
    }

    // Verifica se a URL atual é compatível com o role do usuário
    const expectedPath = loginRedirectRules[role]?.path;
    if (!expectedPath) {
      return false;
    }

    // Se allowedId é 'all', pode acessar qualquer rota do seu nível
    if (allowedId === 'all') {
      return currentPath.startsWith(expectedPath);
    }

    // Verifica se a URL contém o ID permitido
    return currentPath.includes(allowedId);
  };

  const getMeRoute = () => {
    // Verifica se há um token de recuperação na URL
    const urlParams = new URLSearchParams(window.location.search);
    const recoveryToken = urlParams.get('recovery');
    
    // Se há um token de recuperação na URL, não faz a validação automática
    if (recoveryToken) {
      setIsLoading(false);
      return;
    }

    api
      .get('/auth/me')
      .then((response) => {
        const { user } = response.data;
        setSignedIn(true);
        setCurrentUser(response.data.user);
        setCurrentOrganization(response.data.organization);
        // Carrega hierarquia do usuário autenticado
        api.get('/auth/hierarchy').then((hierarchyResp) => {
          setUserHierarchy(hierarchyResp.data?.hierarchy || null);
        }).catch(() => {
          setUserHierarchy(null);
        });

        if (user.passwordResetToken && user.firstAccess) {
          navigate(
            `/acessar?recovery=${user.passwordResetToken}&firstAccess=true`,
          );
        } else {
          // Verifica se o usuário já está em uma URL válida para seu role
          const currentPath = window.location.pathname;
          const expectedRedirectUrl = getRedirectUrl(user.role, user.allowedId);
          
          // Se não está na raiz e a URL atual parece válida, não redireciona
          if (currentPath !== '/' && currentPath !== '/acessar') {
            // Verifica se a URL atual é compatível com o role do usuário
            const isCurrentPathValid = isPathValidForUser(currentPath, user.role, user.allowedId);
            if (isCurrentPathValid) {
              // Usuário já está em uma URL válida, não redireciona
              return;
            }
          }
          
          // Redireciona apenas se necessário
          navigate(expectedRedirectUrl);
        }
      })
      .catch(() => {
        setSignedIn(false);
        setCurrentUser(null);
        removeToken();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const refreshCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data.user);
      setCurrentOrganization(response.data.organization);
    } catch (e) {
      // noop
    }
  };

  const handleLogin = async (credentials) => {
    await api
      .post('/auth/session', credentials)
      .then((response) => {
        const { user } = response.data;
        api.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
        saveToken(user.token);
        setSignedIn(true);
        setCurrentUser(user);
        // Busca hierarquia logo após login
        api.get('/auth/hierarchy').then((hierarchyResp) => {
          setUserHierarchy(hierarchyResp.data?.hierarchy || null);
        }).catch(() => setUserHierarchy(null));

        if (user.passwordResetToken && user.firstAccess) {
          navigate(
            `/acessar?recovery=${user.passwordResetToken}&firstAccess=true`,
          );
        } else {
          navigate(getRedirectUrl(user.role, user.allowedId));
        }
      })
      .catch(() => toast.error('Credenciais não encontradas.'));
  };

  const handleUpdateCurrentOrganization = (organization) => {
    setCurrentOrganization(organization);
  };

  const handleForgot = async (email) => {
    await api
      .post('/auth/forgot_password', { email })
      .then(() => {
        toast.success('Você receberá um e-mail para criar uma nova senha.');
      })
      .catch(() => toast.error('Usuário não encontrado.'));
  };

  const handleReset = async (token, newPassword) => {
    await api
      .post('/auth/reset_password', { token, newPassword })
      .then(() => {
        // Remove o token atual do localStorage para garantir logout
        removeToken();
        setSignedIn(false);
        setCurrentUser(null);
        setCurrentOrganization(null);
        
        toast.success(
          'Sua senha foi redefinida com sucesso. Você já pode entrar utilizando a nova senha.',
        );
        navigate('/acessar');
      })
      .catch(() => toast.error('Erro ao redefinir senha.'));
  };

  const handleLogout = () => {
    removeToken();
    reset();
    navigate('/acessar');
    ocultarJivoChat();
  };

  function mostrarJivoChat() {
    var jivoWidget = document.querySelector('.globalClass_fb40');
    if (jivoWidget) {
      jivoWidget.style.display = 'inline';
    }
  }

  function ocultarJivoChat() {
    var jivoWidget = document.querySelector('.globalClass_fb40');
    if (jivoWidget) {
      jivoWidget.style.display = 'none';
    }
  }

  useEffect(() => {
    getMeRoute();
  }, []);

  const exportData = useMemo(
    () => ({
      isLoading,
      signedIn,
      currentUser,
      currentOrganization,
      userHierarchy,
      handleUpdateCurrentOrganization,
      handleLogin,
      handleLogout,
      handleForgot,
      handleReset,
      getRedirectUrl,
      refreshCurrentUser,
    }),
    [signedIn, currentUser, isLoading, currentOrganization, userHierarchy],
  );

  return (
    <ContextUser.Provider value={exportData}>{children}</ContextUser.Provider>
  );
};

export default UserProvider;
